import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { ApiError } from "@shared-core/data/api/errors";
import { extractFieldErrors } from "@shared-core/data/validation";
import type {
  AccrualUploadOut,
  AccrualUploadPreviewOut,
} from "@shared-core/data/api-types";
import {
  formatCurrencyGbpCompact,
  formatDateTimeOrDash,
} from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import { useCanUploadAccruals } from "../app/auth/hooks";
import { Button } from "../app/components/primitives/button";
import { currentTime } from "@shared-core/domain/current-time";

// AccrualsUploadPage — accounts' month-end accrual/deferral upload.
//
// The flow is deliberately preview-then-commit: nothing is written until the
// user has seen exactly what would move. The file goes up on BOTH steps
// (server-side rule: one parser, and a preview can never be replayed as
// pre-blessed data), so this page never reads a spreadsheet — it holds the
// File and posts it twice.
//
// `expected_current_upload_id` carries the preview's view of the period's live
// batch through to the commit. If somebody else committed in between, the
// server answers 409 and the page sends the user back through preview rather
// than overwriting their work.
//
// Design: docs/features/month-landing-forecast/14_forecasting_accrual_upload_update_spec.md

type Stage =
  | { status: "idle" }
  | { status: "validating" }
  | { status: "previewed"; preview: AccrualUploadPreviewOut }
  | { status: "committing"; preview: AccrualUploadPreviewOut }
  | { status: "committed"; upload: AccrualUploadOut }
  | { status: "stale"; preview: AccrualUploadPreviewOut }
  | { status: "failed"; message: string };

function currentMonthKey(): string {
  const now = currentTime();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(monthKey: string): string {
  return new Date(`${monthKey}-01T00:00:00Z`).toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

function signed(amount: number): string {
  return `${amount < 0 ? "−" : "+"}${formatCurrencyGbpCompact(Math.abs(amount))}`;
}

export function AccrualsUploadPage() {
  const adapter = useDataAdapter();
  const canUpload = useCanUploadAccruals();
  const [periodMonth, setPeriodMonth] = useState(currentMonthKey);
  const [file, setFile] = useState<File | null>(null);
  const [stage, setStage] = useState<Stage>({ status: "idle" });
  const [history, setHistory] = useState<AccrualUploadOut[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);

  const loadHistory = useCallback(async () => {
    if (!canUpload) return;
    try {
      const { uploads } = await adapter.listAccrualUploads();
      setHistory(uploads);
    } catch {
      // History is context, not the task. A failure here must not block an
      // upload, so it degrades to an empty list rather than an error page.
      setHistory([]);
    }
  }, [adapter, canUpload]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // The server is the gate; this only avoids rendering a page whose every
  // request would 403.
  if (!canUpload) {
    return (
      <div className="max-w-3xl space-y-3">
        <h1 className="text-foreground">Accruals &amp; deferrals</h1>
        <p className="text-muted-foreground text-sm">
          This surface is limited to the accounts team. Ask an administrator if
          you need access to the month-end adjustment upload.
        </p>
        <Link
          to={paths.insights()}
          className="text-primary text-sm underline hover:no-underline"
        >
          Back to Insights
        </Link>
      </div>
    );
  }

  const reset = () => {
    setFile(null);
    setStage({ status: "idle" });
    if (fileInput.current) fileInput.current.value = "";
  };

  const selectFile = (next: File | null) => {
    setFile(next);
    // A new file invalidates whatever the last preview said.
    setStage({ status: "idle" });
  };

  const runPreview = async () => {
    if (!file) return;
    setStage({ status: "validating" });
    try {
      const preview = await adapter.previewAccrualUpload({
        file,
        periodMonth,
      });
      setStage({ status: "previewed", preview });
    } catch (err) {
      // A whole-file failure comes back field-tagged on `file`; anything else
      // is reported as-is rather than flattened to "something went wrong".
      const fields = extractFieldErrors(err);
      setStage({
        status: "failed",
        message:
          fields[0]?.message ??
          (err instanceof ApiError && err.detail
            ? err.detail
            : "The file could not be checked. Please try again."),
      });
    }
  };

  const runCommit = async (preview: AccrualUploadPreviewOut) => {
    if (!file) return;
    setStage({ status: "committing", preview });
    try {
      const upload = await adapter.commitAccrualUpload({
        file,
        periodMonth,
        expectedCurrentUploadId: preview.current_upload_id,
      });
      setStage({ status: "committed", upload });
      setFile(null);
      if (fileInput.current) fileInput.current.value = "";
      loadHistory();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setStage({ status: "stale", preview });
        return;
      }
      setStage({
        status: "failed",
        message:
          err instanceof ApiError && err.detail
            ? err.detail
            : "The batch could not be committed. Please try again.",
      });
    }
  };

  const busy = stage.status === "validating" || stage.status === "committing";
  const preview =
    stage.status === "previewed" ||
    stage.status === "committing" ||
    stage.status === "stale"
      ? stage.preview
      : null;

  return (
    <div className="max-w-4xl space-y-6">
      <header className="space-y-2">
        <h1 className="text-foreground">Accruals &amp; deferrals</h1>
        <p className="text-muted-foreground text-sm">
          Upload the month-end working paper of sales brought forward into the
          period and sales deferred out of it. Adjustments overlay the forecast
          — they never change a job, an invoice or anything in Tharstern — and
          appear on the{" "}
          <Link
            to={paths.insights()}
            className="text-primary underline hover:no-underline"
          >
            Month Landing
          </Link>{" "}
          chart as their own strip.
        </p>
        <p className="text-muted-foreground text-sm">
          Re-uploading for a period replaces that period&rsquo;s batch outright.
          Nothing is written until you commit.
        </p>
      </header>

      <section className="border-border bg-surface space-y-4 rounded-lg border p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1">
            <label
              htmlFor="accrual-period"
              className="text-foreground block text-sm font-medium"
            >
              Period
            </label>
            <input
              id="accrual-period"
              type="month"
              value={periodMonth}
              onChange={(e) => {
                setPeriodMonth(e.target.value);
                setStage({ status: "idle" });
              }}
              className="border-border bg-surface rounded-md border px-3 py-2 text-sm"
            />
          </div>
          <div className="space-y-1">
            <label
              htmlFor="accrual-file"
              className="text-foreground block text-sm font-medium"
            >
              Working paper
            </label>
            <input
              id="accrual-file"
              ref={fileInput}
              type="file"
              accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => selectFile(e.target.files?.[0] ?? null)}
              aria-describedby="accrual-file-hint"
              className="text-sm"
            />
            <p id="accrual-file-hint" className="text-muted-foreground text-xs">
              CSV or XLSX in the agreed accounts format.
            </p>
          </div>
          <Button
            type="button"
            onClick={runPreview}
            disabled={!file || busy}
            className="mb-6"
          >
            {stage.status === "validating" ? "Checking…" : "Check file"}
          </Button>
        </div>

        {stage.status === "validating" && (
          <p role="status" className="text-muted-foreground text-sm">
            Checking {file?.name} against {monthLabel(periodMonth)}…
          </p>
        )}

        {stage.status === "failed" && (
          <div
            role="alert"
            className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border px-4 py-3 text-sm"
          >
            <p className="font-medium">The file could not be used.</p>
            <p>{stage.message}</p>
          </div>
        )}

        {stage.status === "stale" && (
          <div
            role="alert"
            className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          >
            <p className="font-medium">
              Somebody else committed for {monthLabel(periodMonth)} while you
              were reviewing.
            </p>
            <p>
              Nothing has been written. Check the file again so the difference
              you approve is measured against what is actually live now.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={runPreview}
              className="mt-2"
            >
              Re-check file
            </Button>
          </div>
        )}

        {stage.status === "committed" && (
          <div
            role="status"
            className="rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900"
          >
            <p className="font-medium">
              {monthLabel(periodMonth)} adjustments committed.
            </p>
            <p>
              {stage.upload.row_count} row
              {stage.upload.row_count === 1 ? "" : "s"} from{" "}
              {stage.upload.filename} are now the live batch
              {stage.upload.replaces_upload_id
                ? ", replacing the previous one"
                : ""}
              . They reach the Month Landing chart on the next nightly snapshot.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={reset}
              className="mt-2"
            >
              Upload another
            </Button>
          </div>
        )}
      </section>

      {preview && <PreviewPanel preview={preview} />}

      {preview && (
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            onClick={() => runCommit(preview)}
            // A stale preview stays stale: re-committing would carry the same
            // expected id and earn the same 409. Re-checking is the way out.
            disabled={!preview.can_commit || busy || stage.status === "stale"}
          >
            {stage.status === "committing"
              ? "Committing…"
              : `Commit ${monthLabel(periodMonth)} adjustments`}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={reset}
            disabled={busy}
          >
            Cancel
          </Button>
          {!preview.can_commit && (
            <p className="text-destructive text-sm">
              Fix the {preview.error_count} blocking problem
              {preview.error_count === 1 ? "" : "s"} above and check the file
              again.
            </p>
          )}
        </div>
      )}

      <UploadHistory uploads={history} />
    </div>
  );
}

// ── Preview ─────────────────────────────────────────────────────────
// What would be committed, and what it changes. Findings come first: a
// blocking problem is the only thing that matters until it is fixed.

function PreviewPanel({ preview }: { preview: AccrualUploadPreviewOut }) {
  const net = preview.rows.reduce(
    (sum, r) => sum + r.adjustment_revenue_amt,
    0,
  );
  const movement =
    preview.diff.new_total_revenue_amt -
    preview.diff.previous_total_revenue_amt;

  return (
    <section className="space-y-4">
      {preview.findings.length > 0 && (
        <div
          className={`rounded-md border px-4 py-3 text-sm ${
            preview.error_count > 0
              ? "border-destructive/40 bg-destructive/5"
              : "border-amber-300 bg-amber-50 text-amber-900"
          }`}
        >
          <p className="font-medium">
            {preview.error_count > 0
              ? `${preview.error_count} problem${preview.error_count === 1 ? "" : "s"} blocking this upload`
              : `${preview.warning_count} thing${preview.warning_count === 1 ? "" : "s"} worth checking`}
            {preview.error_count > 0 && preview.warning_count > 0
              ? `, plus ${preview.warning_count} to check`
              : ""}
            .
          </p>
          <ul className="mt-2 space-y-1">
            {preview.findings.map((f) => (
              <li key={`${f.row_number}-${f.field}-${f.code}`}>
                <span className="font-medium">Row {f.row_number}</span> ·{" "}
                {f.field}: {f.message}
                {f.severity === "warning" && (
                  <span className="italic"> (warning)</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <PreviewStat label="Rows" value={String(preview.rows.length)} />
        <PreviewStat label="Net adjustment" value={signed(net)} />
        <PreviewStat label="Change vs current batch" value={signed(movement)} />
        <PreviewStat
          label="Tharstern jobs added / changed / removed"
          value={`${preview.diff.added_job_nos.length} / ${preview.diff.changed_job_nos.length} / ${preview.diff.removed_job_nos.length}`}
        />
      </div>

      <div className="border-border overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <caption className="sr-only">
            Rows that would be committed for this period
          </caption>
          <thead className="bg-muted/50 text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Row</th>
              <th className="px-3 py-2 text-left font-medium">Job</th>
              <th className="px-3 py-2 text-left font-medium">Direction</th>
              <th className="px-3 py-2 text-right font-medium">Revenue</th>
              <th className="px-3 py-2 text-right font-medium">Contribution</th>
              <th className="px-3 py-2 text-left font-medium">Note</th>
            </tr>
          </thead>
          <tbody>
            {preview.rows.map((row) => (
              <tr key={row.row_number} className="border-border border-t">
                <td className="text-muted-foreground px-3 py-2">
                  {row.row_number}
                </td>
                <td className="px-3 py-2 font-medium">
                  {row.job_no}
                  {!row.is_known_job && (
                    <span className="text-destructive ml-1 text-xs">
                      unknown
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  {row.direction === "deferred"
                    ? "Deferred out"
                    : "Brought forward"}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {signed(row.adjustment_revenue_amt)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {signed(row.adjustment_contribution_amt)}
                </td>
                <td className="text-muted-foreground px-3 py-2">{row.note}</td>
              </tr>
            ))}
            {preview.rows.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="text-muted-foreground px-3 py-6 text-center"
                >
                  No usable rows in this file.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function PreviewStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-border bg-surface rounded-lg border px-3 py-2">
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className="text-foreground text-lg font-medium tabular-nums">
        {value}
      </p>
    </div>
  );
}

// ── History ─────────────────────────────────────────────────────────
// Batch headers are append-only, so this is the audit trail: who replaced
// what, and when.

function UploadHistory({ uploads }: { uploads: AccrualUploadOut[] }) {
  if (uploads.length === 0) return null;
  return (
    <section className="space-y-2">
      <h2 className="text-foreground text-base font-medium">
        Committed batches
      </h2>
      <div className="border-border overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Period</th>
              <th className="px-3 py-2 text-left font-medium">File</th>
              <th className="px-3 py-2 text-right font-medium">Rows</th>
              <th className="px-3 py-2 text-left font-medium">Uploaded</th>
              <th className="px-3 py-2 text-left font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {uploads.map((upload, index) => {
              // Only the newest header for a period holds rows — that is what
              // makes "the current batch" a fact about the data rather than a
              // flag that could disagree with it.
              const isCurrent =
                uploads.findIndex(
                  (u) => u.period_month === upload.period_month,
                ) === index;
              return (
                <tr key={upload.id} className="border-border border-t">
                  <td className="px-3 py-2">
                    {monthLabel(upload.period_month.slice(0, 7))}
                  </td>
                  <td className="px-3 py-2">{upload.filename}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {upload.row_count}
                  </td>
                  <td className="text-muted-foreground px-3 py-2">
                    {formatDateTimeOrDash(upload.uploaded_at)}
                  </td>
                  <td className="px-3 py-2">
                    {isCurrent ? (
                      <span className="font-medium text-emerald-700">Live</span>
                    ) : (
                      <span className="text-muted-foreground">Superseded</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
